import { useState, useRef, useEffect, useId } from "react";
import citiesData from "../../data/cities.json" with { type: "json" };
import countriesData from "../../data/countries.json" with { type: "json" };

const cities: string[] = citiesData as string[];
const countries: string[] = countriesData as string[];

const MAX_SUGGESTIONS = 8;

function getSuggestions(dataset: string[], query: string): string[] {
  if (!query.trim()) return dataset.slice(0, MAX_SUGGESTIONS);
  const q = query.toLowerCase();
  const starts = dataset.filter((s) => s.toLowerCase().startsWith(q));
  const contains = dataset.filter(
    (s) => !s.toLowerCase().startsWith(q) && s.toLowerCase().includes(q)
  );
  return [...starts, ...contains].slice(0, MAX_SUGGESTIONS);
}

interface LocationAutocompleteProps {
  type: "city" | "country";
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputStyle?: React.CSSProperties;
  className?: string;
}

export function LocationAutocomplete({
  type,
  value,
  onChange,
  placeholder,
  inputStyle,
  className,
}: LocationAutocompleteProps) {
  const dataset = type === "city" ? cities : countries;
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const suggestions = open ? getSuggestions(dataset, value) : [];

  useEffect(() => {
    setHighlighted(0);
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function select(item: string) {
    onChange(item);
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (suggestions[highlighted]) select(suggestions[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} style={{ position: "relative" }} className={className}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder={placeholder}
        maxLength={100}
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && suggestions[highlighted] ? `${listId}-${highlighted}` : undefined}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        className="w-full rounded-lg text-sm transition-all focus-visible:outline-none"
        style={{
          border: "1px solid var(--color-border)",
          background: "var(--color-background)",
          color: "var(--color-foreground)",
          padding: "0.5rem 0.75rem",
          height: "2.25rem",
          ...inputStyle,
        }}
        onFocusCapture={(e) => (e.currentTarget.style.borderColor = "var(--color-border-strong)")}
        onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
      />

      {open && suggestions.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 50,
            background: "var(--color-card)",
            border: "1px solid var(--color-border-strong)",
            borderRadius: "0.5rem",
            boxShadow: "var(--shadow-md)",
            maxHeight: "14rem",
            overflowY: "auto",
            padding: "0.25rem",
            margin: 0,
            listStyle: "none",
          }}
        >
          {suggestions.map((item, i) => (
            <li
              key={item}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === highlighted}
              onMouseDown={(e) => {
                e.preventDefault();
                select(item);
              }}
              onMouseEnter={() => setHighlighted(i)}
              style={{
                padding: "0.4rem 0.75rem",
                borderRadius: "0.375rem",
                cursor: "pointer",
                fontSize: "0.875rem",
                color: i === highlighted ? "var(--color-accent)" : "var(--color-foreground)",
                background: i === highlighted ? "var(--color-accent-subtle)" : "transparent",
                transition: "background 0.1s",
              }}
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
